'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Heart, LogIn, LogOut, UserCircle, UserPlus, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { trackDonateNowClick } from '@/lib/analytics';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Donate', href: '/donation-screen' },
  { label: 'Membership', href: '/member-profile-page' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, loading, signOut } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/');
      router.refresh();
    } catch {}
  };

  const isStaff = user?.user_metadata?.role === 'staff' || user?.user_metadata?.role === 'admin';

  const textColor = scrolled ? '#1a1200' : '#ffffff';
  const subTextColor = scrolled ? '#7a6030' : 'rgba(255,255,255,0.8)';
  const navTextColor = scrolled ? '#3d2c00' : 'rgba(255,255,255,0.9)';
  const navHoverBg = scrolled ? 'rgba(196,154,0,0.1)' : 'rgba(255,255,255,0.15)';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
      style={scrolled ? { background: 'rgba(255,254,245,0.97)', borderBottom: '1px solid #e8d88a' } : {}}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group">
            <AppLogo size={40} />
            <div className="flex flex-col">
              <span className="font-extrabold text-lg leading-tight tracking-tight transition-colors" style={{ color: textColor }}>
                MOURNDIECE
              </span>
              <span className="text-xs font-medium tracking-widest uppercase transition-colors" style={{ color: subTextColor }}>
                Foundation
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`nav-${link?.label}`}
                href={link?.href}
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                style={{ color: navTextColor }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = navHoverBg; (e.currentTarget as HTMLElement).style.color = '#C49A00'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = navTextColor; }}
              >
                {link?.label}
              </Link>
            ))}
            {isStaff && (
              <Link
                href="/staff-dashboard"
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                style={{ color: navTextColor }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = navHoverBg; (e.currentTarget as HTMLElement).style.color = '#C49A00'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = navTextColor; }}
              >
                Staff Dashboard
              </Link>
            )}
            {isStaff && (
              <Link
                href="/admin"
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                style={{ color: navTextColor }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = navHoverBg; (e.currentTarget as HTMLElement).style.color = '#C49A00'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = navTextColor; }}
              >
                Admin
              </Link>
            )}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {!loading && (
              user ? (
                <>
                  {isStaff && (
                    <Link
                      href="/staff-dashboard"
                      className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-150 border"
                      style={{ color: '#C49A00', borderColor: '#C49A00' }}
                    >
                      <Shield size={15} />
                      Staff Panel
                    </Link>
                  )}
                  <Link
                    href="/member-account"
                    className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-150 border"
                    style={{ color: '#C49A00', borderColor: '#C49A00' }}
                  >
                    <UserCircle size={15} />
                    My Account
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-150"
                    style={{ color: scrolled ? '#7a6030' : 'rgba(255,255,255,0.7)' }}
                  >
                    <LogOut size={15} />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-150 border"
                    style={{ color: scrolled ? '#C49A00' : '#ffffff', borderColor: scrolled ? '#C49A00' : 'rgba(255,255,255,0.6)' }}
                  >
                    <LogIn size={15} />
                    Sign In
                  </Link>
                  <Link
                    href="/signup"
                    className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-150"
                    style={{ background: 'linear-gradient(135deg, #C49A00, #9A7800)', color: '#ffffff' }}
                  >
                    <UserPlus size={15} />
                    Join Now
                  </Link>
                </>
              )
            )}
            <Link
              href="/donation-screen"
              className="flex items-center gap-2 btn-primary text-sm font-semibold px-5 py-2.5 rounded-lg shadow-primary-glow"
              onClick={() => trackDonateNowClick('header-desktop')}
            >
              <Heart size={15} />
              Donate Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg transition-colors"
            style={{ color: scrolled ? '#C49A00' : '#ffffff' }}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="border-t px-6 py-4 space-y-1" style={{ background: '#fffef5', borderColor: '#e8d88a' }}>
          {navLinks?.map((link) => (
            <Link
              key={`mobile-nav-${link?.label}`}
              href={link?.href}
              onClick={() => setMobileOpen(false)}
              className="block px-4 py-3 rounded-lg text-sm font-semibold transition-colors"
              style={{ color: '#3d2c00' }}
            >
              {link?.label}
            </Link>
          ))}
          {isStaff && (
            <Link
              href="/staff-dashboard"
              onClick={() => setMobileOpen(false)}
              className="block px-4 py-3 rounded-lg text-sm font-semibold transition-colors"
              style={{ color: '#C49A00' }}
            >
              Staff Dashboard
            </Link>
          )}
          <div className="pt-3 pb-1 space-y-2 border-t mt-2" style={{ borderColor: '#e8d88a' }}>
            {!loading && (
              user ? (
                <>
                  <Link
                    href="/member-account"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 text-center text-sm font-semibold px-4 py-2.5 rounded-lg border transition-colors"
                    style={{ color: '#C49A00', borderColor: '#C49A00' }}
                  >
                    <UserCircle size={15} />
                    My Account
                  </Link>
                  <button
                    onClick={() => { setMobileOpen(false); handleSignOut(); }}
                    className="w-full flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                    style={{ color: '#7a6030', background: '#fdf8e8' }}
                  >
                    <LogOut size={15} />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg border transition-colors"
                    style={{ color: '#C49A00', borderColor: '#C49A00' }}
                  >
                    <LogIn size={15} />
                    Sign In
                  </Link>
                  <Link
                    href="/signup"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                    style={{ background: 'linear-gradient(135deg, #C49A00, #9A7800)', color: '#ffffff' }}
                  >
                    <UserPlus size={15} />
                    Join Now
                  </Link>
                </>
              )
            )}
            <Link
              href="/donation-screen"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 btn-primary text-sm font-semibold px-4 py-2.5 rounded-lg"
            >
              <Heart size={15} />
              Donate Now
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}